import type { RouteName, RouteConfig } from './route-definitions'
import { routeDefinitions } from './route-definitions'

/**
 * Extract parameter names from a route pattern.
 * Example: '/users/:id' -> { id: string }
 */
type ExtractRouteParams<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
  ? { [K in Param]: string } & ExtractRouteParams<Rest>
  : T extends `${string}:${infer Param}`
    ? { [K in Param]: string }
    : Record<string, never>

/**
 * Get the parameter type for a specific route
 */
export type RouteParams<T extends string> = ExtractRouteParams<T>

/**
 * Get specific route's parameter type by route name
 */
export type RouteParamsByName<K extends RouteName> = ExtractRouteParams<
  (typeof routeDefinitions)[K]['path']
>

/**
 * Build a path with parameters
 * @param path - The route path pattern
 * @param params - The parameters to replace in the pattern
 * @returns The built path string
 */
function buildPath<P extends string>(path: P, params?: ExtractRouteParams<P>): string {
  let builtPath: string = path

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      builtPath = builtPath.replace(`:${key}`, encodeURIComponent(String(value)))
    })
  }

  return builtPath
}

/**
 * Type-safe path builders for all routes
 * This provides a centralized way to build paths with proper type checking
 */
export const routePaths = Object.entries(routeDefinitions).reduce(
  (acc, [name, definition]) => {
    const routeName = name as RouteName

    // Create a path builder function for each route
    acc[routeName] = (params?: RouteParamsByName<typeof routeName>) => {
      if (params && Object.keys(params).length > 0) {
        return buildPath(definition.path, params)
      }
      return definition.path
    }

    return acc
  },
  {} as { [K in RouteName]: (params?: RouteParamsByName<K>) => string }
)

/**
 * Check if a route requires parameters
 */
export function routeRequiresParams<K extends RouteName>(
  routeName: K
): routeName is K &
  { [P in K]: RouteParamsByName<P> extends Record<string, never> ? never : P }[K] {
  return routeDefinitions[routeName].path.includes(':')
}

/**
 * Get route by path pattern matching
 * This is useful for matching current location to route definition
 */
export function findRouteByPath(pathname: string): {
  name: RouteName
  definition: RouteConfig
  params?: Record<string, string>
} | null {
  // Remove trailing slash for consistent matching
  const normalizedPath = pathname.replace(/\/$/, '') || '/'

  // Try to find an exact match first
  for (const [name, definition] of Object.entries(routeDefinitions) as [RouteName, RouteConfig][]) {
    if (definition.path === normalizedPath) {
      return { name, definition }
    }
  }

  // Try pattern matching for dynamic routes
  for (const [name, definition] of Object.entries(routeDefinitions) as [RouteName, RouteConfig][]) {
    const paramNames: string[] = []
    const pattern = definition.path.replace(/:([^/]+)/g, (_, paramName) => {
      paramNames.push(paramName)
      return '([^/]+)'
    })

    const regex = new RegExp(`^${pattern}$`)
    const match = normalizedPath.match(regex)

    if (match) {
      const params = paramNames.reduce<Record<string, string>>((acc, paramName, index) => {
        acc[paramName] = match[index + 1]
        return acc
      }, {})

      return { name, definition, params }
    }
  }

  return null
}
